import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Group,
  Paper,
  SegmentedControl,
  Skeleton,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { jsonGet } from "../api";

/**
 * Which sources the AI surfaces read, pooled across every brand we sample.
 *
 * Per brand this is advice; pooled it is market intelligence, and it answers a question no single
 * customer's data can. A source dropping out of the index reads as ordinary week-to-week movement
 * in one brand's numbers and is unmistakable across all of them at once — which is why "vanished"
 * is its own table rather than a row with a zero.
 *
 * Share, not count, is the comparable figure. Raw citations rise whenever a customer is added, so
 * a domain can look like it is growing when only our sampling grew.
 */

const WINDOWS = [
  { value: "7", label: "7 days" },
  { value: "28", label: "28 days" },
  { value: "90", label: "90 days" },
];

const pct = (v) => `${(v * 100).toFixed(1)}%`;

function ShareMove({ current, previous }) {
  if (previous === 0 && current > 0) {
    return (
      <Badge size="xs" variant="light" color="blue">
        new
      </Badge>
    );
  }
  if (previous === 0) return null;

  const change = (current - previous) / previous;
  if (Math.abs(change) < 0.2) return null;

  return (
    <Badge size="xs" variant="light" color={change > 0 ? "teal" : "orange"}>
      {change > 0 ? "▲" : "▼"} {Math.round(Math.abs(change) * 100)}%
    </Badge>
  );
}

export default function GlobalSourcesPanel() {
  const [days, setDays] = useState("28");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await jsonGet(`admin/sources?days=${days}`));
    } catch (e) {
      setError(e?.message ?? "Could not load the source index");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Paper shadow="xs" p="md" mb="md">
      <Group justify="space-between" align="flex-start" mb="xs">
        <div>
          <Title order={4}>What the AI surfaces read</Title>
          <Text fz="sm" c="dimmed">
            Every brand pooled. Unaided prompts only, one citation per answer.
          </Text>
        </div>
        <SegmentedControl value={days} onChange={setDays} data={WINDOWS} size="xs" />
      </Group>

      {loading && <Skeleton h={320} radius="md" />}

      {error && (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
          {error}
        </Alert>
      )}

      {data && !loading && (
        <>
          <Text fz="xs" c="dimmed" mb="sm">
            {data.answers.toLocaleString()} answers this window ·{" "}
            {data.previousAnswers.toLocaleString()} in the one before
          </Text>

          <Table.ScrollContainer minWidth={560}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Source</Table.Th>
                  <Table.Th w={110}>Answers</Table.Th>
                  <Table.Th w={130}>Share</Table.Th>
                  <Table.Th w={90}>Brands</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.sources.map((s) => (
                  <Table.Tr key={s.domain}>
                    <Table.Td>
                      <Text fz="sm">{s.domain}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm">{s.citations.toLocaleString()}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Text fz="sm">{pct(s.shareOfAnswers)}</Text>
                        <ShareMove
                          current={s.shareOfAnswers}
                          previous={s.previousShareOfAnswers}
                        />
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="sm" c="dimmed">
                        {s.brands}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>

          {data.vanished?.length > 0 && (
            <>
              <Title order={5} mt="lg">
                Stopped being cited
              </Title>
              <Text fz="xs" c="dimmed" mb="xs">
                Cited at least five times in the previous window and not once in this one. This is
                the shape a source de-indexing takes, and it cannot appear in the table above —
                there is no row for a domain with no citations.
              </Text>
              <Group gap="xs">
                {data.vanished.map((s) => (
                  <Badge key={s.domain} variant="light" color="orange">
                    {s.domain} · was {s.previousCitations}
                  </Badge>
                ))}
              </Group>
            </>
          )}
        </>
      )}
    </Paper>
  );
}
